<template>
  <div class="container py-4">
    <div class="page-card">
      <div
        class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2"
      >
        <div>
          <h4 class="mb-0">Users & permissions</h4>
          <p class="small text-muted mb-0">
            Manage staff accounts and per-store access
          </p>
        </div>

        <icon-button
          :func="() => openCreate()"
          :icon="'plus'"
          :title="'Add user'"
          :classes="'akkurate-green-btn btn-size2'"
          :btnName="'Add user'"
        />
      </div>

      <b-table
        :items="users"
        :fields="fields"
        :busy="loading"
        responsive
        hover
        small
      >
        <template #cell(name)="data">
          {{ data.item.firstName }} {{ data.item.lastName }}
        </template>
        <template #cell(isActive)="data">
          <b-badge
            :variant="data.value ? 'success' : 'secondary'"
            :class="statusBadge(data.value)"
          >
            {{ data.value ? "Active" : "Inactive" }}
          </b-badge>
        </template>
        <template #cell(stores)="data">
          <span
            v-if="!data.item.stores?.length"
            class="text-muted badge akkurate-gray-bg"
            >None</span
          >
          <b-badge
            v-for="store in data.item.stores"
            :key="store.storeId"
            variant="light"
            class="me-1 mb-1 badge akkurate-gray-bg"
          >
            {{ store.storeName }}
          </b-badge>
        </template>
        <template #cell(actions)="data">
          <icon-button
            :func="() => openEdit(data.item)"
            :icon="'eye'"
            :title="'Edit user'"
            :classes="'akkurate-warning btn-sm'"
            :btnName="''"
          />
          <icon-button
            :func="() => openPermissions(data.item)"
            :icon="'door-open'"
            :title="'Manage permissions'"
            :classes="'akkurate-gray-btn btn-sm'"
            :btnName="''"
          />
        </template>
      </b-table>
    </div>

    <!-- Create / edit user -->
    <b-modal
      v-model="showUserForm"
      :title="editingUser ? 'Edit user' : 'New user'"
      @ok="saveUser"
    >
      <b-form-group label="First name">
        <b-form-input
          class="form-control"
          v-model="userForm.firstName"
          required
        />
      </b-form-group>
      <b-form-group label="Last name">
        <b-form-input
          class="form-control"
          v-model="userForm.lastName"
          required
        />
      </b-form-group>
      <b-form-group label="Email">
        <b-form-input
          class="form-control"
          v-model="userForm.email"
          type="email"
          required
        />
      </b-form-group>
      <b-form-group
        :label="editingUser ? 'New password (leave blank to keep)' : 'Password'"
      >
        <b-form-input
          class="form-control"
          v-model="userForm.password"
          type="password"
          autocomplete="new-password"
        />
      </b-form-group>
      <b-form-group label="Role label">
        <b-form-select
          class="akkurate_input filter-list filter-list-choice status-filter custom-select form-select"
          v-model="userForm.role"
          :options="roleOptions"
        />
        <b-form-text
          >Display role only — access is controlled by store
          permissions</b-form-text
        >
      </b-form-group>
      <b-form-group v-if="editingUser" label="Status">
        <b-form-checkbox v-model="userForm.isActive">Active</b-form-checkbox>
      </b-form-group>

      <div v-if="!editingUser" class="mt-3">
        <h6>Initial store access</h6>
        <div
          v-for="(assignment, idx) in userForm.storeAssignments"
          :key="idx"
          class="border rounded p-2 mb-2"
        >
          <b-form-group label="Store" class="mb-2">
            <b-form-select
              v-model="assignment.storeId"
              class="akkurate_input filter-list filter-list-choice status-filter custom-select form-select"
              :options="storeSelectOptions"
              size="sm"
            />
          </b-form-group>
          <permission-checkboxes
            v-model="assignment.permissions"
            :definitions="permissionDefs"
          />

          <icon-button
            v-if="userForm.storeAssignments.length > 1"
            :func="() => userForm.storeAssignments.splice(idx, 1)"
            :icon="'times'"
            :title="'Remove store'"
            :classes="'akkurate-danger-bg btn-sm mt-2'"
            :btnName="''"
          />
        </div>
        <icon-button
          :func="() => addStoreAssignment()"
          :icon="'plus'"
          :title="'Add store'"
          :classes="'akkurate-green-btn btn-size2 mt-2'"
          :btnName="'Add store'"
        />
      </div>

      <template #modal-footer>
        <icon-button
          :func="() => (showUserForm = false)"
          :icon="'times'"
          :title="'Close'"
          :classes="'akkurate-danger-bg btn-size2'"
          :btnName="'Close'"
        />
        <icon-button
          :func="() => saveUser()"
          :icon="'check'"
          :title="'Save user'"
          :classes="'akkurate-green-btn btn-size2'"
          :btnName="'Save user'"
          :disabled="isBusy"
        />
      </template>
    </b-modal>

    <!-- Per-user permissions -->
    <b-modal
      v-model="showPermissions"
      :title="
        permissionsUser
          ? `Permissions — ${permissionsUser.firstName} ${permissionsUser.lastName}`
          : 'Permissions'
      "
      size="lg"
      ok-only
      ok-title="Done"
    >
      <div v-if="permissionsUser">
        <div
          v-for="store in permissionsUser.stores"
          :key="store.storeId"
          class="border rounded p-3 mb-3"
        >
          <div class="d-flex justify-content-between align-items-center mb-2">
            <strong>{{ store.storeName }}</strong>

            <icon-button
              :func="() => removeStore(permissionsUser.id, store.storeId)"
              :icon="'times'"
              :title="'Remove store'"
              :classes="'akkurate-danger-bg btn-sm'"
              :btnName="''"
              :disabled="isCurrentUser(permissionsUser.id)"
            />
          </div>
          <permission-checkboxes
            v-model="store.permissions"
            :definitions="permissionDefs"
            @input="
              saveStorePermissions(
                permissionsUser.id,
                store.storeId,
                store.permissions,
              )
            "
          />
        </div>

        <div v-if="!permissionsUser.stores?.length" class="text-muted mb-3">
          No store assignments yet.
        </div>

        <div class="border rounded p-3">
          <h6 class="mb-2">Assign to store</h6>
          <b-form-group label="Store" class="mb-2">
            <b-form-select
              v-model="assignForm.storeId"
              class="akkurate_input filter-list filter-list-choice status-filter custom-select form-select"
              :options="availableStoreOptions"
              size="sm"
            />
          </b-form-group>
          <permission-checkboxes
            v-model="assignForm.permissions"
            :definitions="permissionDefs"
          />

          <icon-button
            :func="() => assignStore()"
            :icon="'check'"
            :title="'Assign store'"
            :classes="'akkurate-green-btn btn-size2 mt-2'"
            :btnName="'Assign store'"
            :disabled="!assignForm.storeId"
          />
        </div>
      </div>

      <template #modal-footer>
        <icon-button
          :func="() => (showPermissions = false)"
          :icon="''"
          :title="'Done'"
          :classes="'akkurate-green-bg btn-size2'"
          :btnName="'Done'"
        />
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import ApiService from "@/core/services/api.service";
import PermissionCheckboxes from "./PermissionCheckboxes.vue";
import IconButton from "@/components/IconButton.vue";

const emptyAssignment = () => ({
  storeId: "",
  permissions: ["SALE", "RECEIPT"],
});

export default {
  name: "Users",
  components: { PermissionCheckboxes, IconButton },
  data() {
    return {
      users: [],
      options: { stores: [], permissions: [], roles: [] },
      loading: false,
      isBusy: false,
      showUserForm: false,
      showPermissions: false,
      editingUser: null,
      permissionsUser: null,
      userForm: {
        firstName: "",
        lastName: "",
        email: "",
        password: "",
        role: "Cashier",
        isActive: true,
        storeAssignments: [emptyAssignment()],
      },
      assignForm: {
        storeId: "",
        permissions: ["SALE", "RECEIPT"],
      },
      fields: [
        { key: "name", label: "Name" },
        { key: "email", label: "Email" },
        { key: "role", label: "Role" },
        { key: "stores", label: "Stores" },
        { key: "isActive", label: "Status" },
        { key: "actions", label: "Actions" },
      ],
      savePermissionsTimer: null,
    };
  },
  computed: {
    ...mapGetters(["currentUser"]),
    permissionDefs() {
      return this.options.permissions || [];
    },
    roleOptions() {
      return (this.options.roles || []).map((r) => ({ value: r, text: r }));
    },
    storeSelectOptions() {
      return [
        { value: "", text: "Select store" },
        ...(this.options.stores || []).map((s) => ({
          value: s.id,
          text: `${s.name} (${s.code})`,
        })),
      ];
    },
    availableStoreOptions() {
      if (!this.permissionsUser) return this.storeSelectOptions;
      const assigned = new Set(
        (this.permissionsUser.stores || []).map((s) => s.storeId),
      );
      return [
        { value: "", text: "Select store" },
        ...(this.options.stores || [])
          .filter((s) => !assigned.has(s.id))
          .map((s) => ({ value: s.id, text: `${s.name} (${s.code})` })),
      ];
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    isCurrentUser(userId) {
      return this.currentUser?.id === userId;
    },
    async load() {
      this.loading = true;
      try {
        const [users, options] = await Promise.all([
          ApiService.get("users"),
          ApiService.get("users/options"),
        ]);
        this.users = users;
        this.options = options;
      } catch (e) {
        toast.fire({
          title: e?.response?.data?.message || "Failed to load users",
          icon: "danger",
        });
      } finally {
        this.loading = false;
      }
    },
    openCreate() {
      this.editingUser = null;
      this.userForm = {
        firstName: "",
        lastName: "",
        email: "",
        password: "",
        role: "Cashier",
        isActive: true,
        storeAssignments: [emptyAssignment()],
      };
      this.showUserForm = true;
    },
    openEdit(user) {
      this.editingUser = user;
      this.userForm = {
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        password: "",
        role: user.role,
        isActive: user.isActive,
        storeAssignments: [],
      };
      this.showUserForm = true;
    },
    addStoreAssignment() {
      this.userForm.storeAssignments.push(emptyAssignment());
    },
    async saveUser() {
      // bvModalEvt.preventDefault();
      try {
        this.isBusy = true;
        if (this.editingUser) {
          const payload = {
            firstName: this.userForm.firstName,
            lastName: this.userForm.lastName,
            email: this.userForm.email,
            role: this.userForm.role,
            isActive: this.userForm.isActive,
          };
          if (this.userForm.password) {
            payload.password = this.userForm.password;
          }
          await ApiService.put(`users/${this.editingUser.id}`, payload);
        } else {
          const assignments = this.userForm.storeAssignments.filter(
            (a) => a.storeId,
          );
          await ApiService.post("users", {
            firstName: this.userForm.firstName,
            lastName: this.userForm.lastName,
            email: this.userForm.email,
            password: this.userForm.password,
            role: this.userForm.role,
            storeAssignments: assignments,
          });
        }
        this.isBusy = false;
        this.showUserForm = false;
        await this.load();
        toast.fire({
          title: "User saved",
          icon: "success",
        });
      } catch (e) {
        toast.fire({
          title: e?.response?.data?.message || "Save failed",
          icon: "danger",
        });
      }
    },
    openPermissions(user) {
      this.permissionsUser = JSON.parse(JSON.stringify(user));
      this.assignForm = { storeId: "", permissions: ["SALE", "RECEIPT"] };
      this.showPermissions = true;
    },
    saveStorePermissions(userId, storeId, permissions) {
      clearTimeout(this.savePermissionsTimer);
      this.savePermissionsTimer = setTimeout(async () => {
        try {
          const updated = await ApiService.patch(
            `users/${userId}/stores/${storeId}/permissions`,
            { permissions: this.normalizePermissions(permissions) },
          );
          this.syncUserInList(updated);
          if (this.permissionsUser?.id === userId) {
            this.permissionsUser = JSON.parse(JSON.stringify(updated));
          }
        } catch (e) {
          toast.fire({
            title: e?.response?.data?.message || "Update failed",
            icon: "danger",
          });
        }
      }, 400);
    },
    normalizePermissions(perms) {
      if (perms.includes("ALL")) return ["ALL"];
      return perms;
    },
    async assignStore() {
      if (!this.permissionsUser || !this.assignForm.storeId) return;
      try {
        const updated = await ApiService.post(
          `users/${this.permissionsUser.id}/stores`,
          {
            storeId: this.assignForm.storeId,
            permissions: this.normalizePermissions(this.assignForm.permissions),
          },
        );
        this.permissionsUser = JSON.parse(JSON.stringify(updated));
        this.syncUserInList(updated);
        this.assignForm = { storeId: "", permissions: ["SALE", "RECEIPT"] };
        toast.fire({
          title: "Store assigned",
          icon: "success",
        });
      } catch (e) {
        toast.fire({
          title: e?.response?.data?.message || "Assign failed",
          icon: "danger",
        });
      }
    },
    async removeStore(userId, storeId) {
      if (!confirm("Remove this user from the store?")) return;
      try {
        const updated = await ApiService.delete(
          `users/${userId}/stores/${storeId}`,
        );
        this.permissionsUser = JSON.parse(JSON.stringify(updated));
        this.syncUserInList(updated);
        toast.fire({
          title: "Store removed",
          icon: "success",
        });
      } catch (e) {
        toast.fire({
          title: e?.response?.data?.message || "Remove failed",
          icon: "danger",
        });
      }
    },
    syncUserInList(updated) {
      const idx = this.users.findIndex((u) => u.id === updated.id);
      if (idx >= 0) {
        this.$set(this.users, idx, updated);
      }
    },
  },
};
</script>
